import './KbArticleEdit.css'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { kbArticleDeleteSpec, kbArticleGetSpec, kbArticleSaveSpec, type KbArticleRow } from 'aevocam-rest-api-spec'
import { KbArticleEditAssets } from './KbArticleEditAssets'
import { KbContentDiff } from './KbContentDiff'
import { BasicPage } from '@/components/layout/BasicPage'
import { TextBox } from '@/components/scalar/TextBox'
import { TextArea } from '@/components/scalar/TextArea'
import { Button } from '@/components/action/Button'
import { TabBar } from '@/components/action/TabBar'
import { toastError, toastInfo, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { AgentChat } from '@/components/agentChat/AgentChat'

type ContentTab = 'draft' | 'published'
type PreviewTab = 'preview' | 'differences'

const NEW_ARTICLE_TEMPLATE = 'Hi there!'

export function KbArticleEdit() {
  const { id } = useParams<{ id: 'new' | string }>()
  const [queryParams] = useSearchParams()
  const location = useLocation()
  const api = useApi({ requireLogin: true, adminOnly: true })
  const navigate = useNavigate()

  const [article, setArticle] = useState<KbArticleRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [path, setPath] = useState('')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [redirectUrl, setRedirectUrl] = useState('')
  const [published, setPublished] = useState(false)
  const [publishAt, setPublishAt] = useState('')
  const [unpublishAt, setUnpublishAt] = useState('')
  const [contentPublic, setContentPublic] = useState('')
  const [contentDraft, setContentDraft] = useState<string | null>(null)
  const [workingDraft, setWorkingDraft] = useState('')
  const [contentTab, setContentTab] = useState<ContentTab>('draft')
  const [previewTab, setPreviewTab] = useState<PreviewTab>('preview')

  const showVersionControls = id !== 'new'

  const savedDraftText = contentDraft ?? contentPublic
  const versionsDiverged = workingDraft !== contentPublic
  const hasSavedDraft = contentDraft != null && contentDraft !== contentPublic
  const hasPendingDraft = versionsDiverged || hasSavedDraft

  const isMetadataDirty = useMemo(() => {
    if (!article) return false
    return (
      path !== (article.path ?? '')
      || title !== (article.title ?? '')
      || subtitle !== (article.subtitle ?? '')
      || redirectUrl !== (article.redirectUrl ?? '')
      || publishAt !== (article.publishAt ? String(article.publishAt) : '')
      || unpublishAt !== (article.unpublishAt ? String(article.unpublishAt) : '')
    )
  }, [article, path, title, subtitle, redirectUrl, publishAt, unpublishAt])

  const isContentDirty = workingDraft !== savedDraftText
  const isDirty = isMetadataDirty || isContentDirty

  useEffect(() => {
    if (!hasPendingDraft && previewTab === 'differences') {
      setPreviewTab('preview')
    }
  }, [hasPendingDraft, previewTab])

  const loadArticle = useCallback((value: KbArticleRow, options?: {
    /** Keep draft/published and preview/differences tab selection */
    preserveView?: boolean
  }) => {
    const pub = value.contentPublic ?? ''
    const draft = value.contentDraft ?? null
    setArticle(value)
    setPath(value.path ?? '')
    setTitle(value.title ?? '')
    setSubtitle(value.subtitle ?? '')
    setRedirectUrl(value.redirectUrl ?? '')
    setPublished(value.published ?? false)
    setPublishAt(value.publishAt ? String(value.publishAt) : '')
    setUnpublishAt(value.unpublishAt ? String(value.unpublishAt) : '')
    setContentPublic(pub)
    setContentDraft(draft)
    setWorkingDraft(draft ?? pub)
    if (!options?.preserveView) {
      setContentTab('draft')
      setPreviewTab('preview')
    }
  }, [])

  const fetchArticle = useCallback(async (options?: {
    quietly?: boolean
    pathQuery?: string | null
  }) => {
    if (!id || id === 'new') return false

    if (!options?.quietly) setLoading(true)

    try {
      const res = await api.request(kbArticleGetSpec, {
        path: { article: id },
        query: {
          forAdmin: 'true',
          path: options?.pathQuery ?? undefined,
        },
      })

      if (res.status === 200 && res.data?.article) {
        const nextArticle = res.data.article
        loadArticle(nextArticle, { preserveView: options?.quietly })
        const editUrl = routes.adminKbArticleEdit(String(nextArticle.id))
        if (editUrl !== location.pathname) {
          navigate(editUrl, { replace: true })
        }
        return true
      }

      toastError(res.error ?? 'Failed to load article')
      return false
    } catch {
      toastError('Failed to load article')
      return false
    } finally {
      if (!options?.quietly) setLoading(false)
    }
  }, [id, loadArticle, location.pathname, navigate])

  const pendingAgentRefreshRef = useRef(false)

  function handleAgentRefresh() {
    if (!pendingAgentRefreshRef.current) return
    pendingAgentRefreshRef.current = false
    void fetchArticle({ quietly: true }).then(ok => {
      if (ok) toastInfo('Article contents have changed')
    })
  }

  useEffect(() => {
    async function fn() {
      if (id === 'new') {

        const article: KbArticleRow = {
          id: -1n,
          path: '/some-article',
          title: 'New article',
          subtitle: 'New article subtitle',
          published: false,
          publishAt: null,
          unpublishAt: null,
          retiredAt: null,
          redirectUrl: null,
          contentPublic: '',
          contentDraft: null,
        }
        loadArticle(article)
        setWorkingDraft(NEW_ARTICLE_TEMPLATE)
        setLoading(false)
        return
      }  // id === 'new'

      await fetchArticle({ pathQuery: queryParams.get('path') })
    }
    void fn()
  }, [id])

  function normalizeDraft(draft: string, publicText: string): string | null {
    return draft === publicText ? null : draft
  }

  function buildArticleBody(overrides: {
    contentPublic?: string
    contentDraft?: string | null
    published?: boolean
    retiredAt?: Date | string | null
  }): KbArticleRow {
    return {
      ...article!,
      path: path || null,
      title: title || null,
      subtitle: subtitle || null,
      redirectUrl: redirectUrl || null,
      published: overrides.published ?? published,
      publishAt: publishAt || null,
      unpublishAt: unpublishAt || null,
      retiredAt: overrides.retiredAt !== undefined
        ? overrides.retiredAt
        : (article!.retiredAt ?? null),
      contentPublic: overrides.contentPublic ?? contentPublic,
      contentDraft: overrides.contentDraft !== undefined
        ? overrides.contentDraft
        : normalizeDraft(workingDraft, overrides.contentPublic ?? contentPublic),
    }
  }

  async function persistArticle(
    body: KbArticleRow,
    options?: {
      successMessage?: ReactNode
      /** When set, keep this published flag if the server returns something else */
      expectedPublished?: boolean
    },
  ) {
    if (!id || !article) return false

    const res = await api.request(kbArticleSaveSpec, {
      path: { article: String(article.id) },
      body: { article: body },
    })

    if (res.status === 200 && res.data?.article) {
      let savedArticle = res.data.article
      if (
        options?.expectedPublished !== undefined
        && savedArticle.published !== options.expectedPublished
      ) {
        savedArticle = { ...savedArticle, published: options.expectedPublished }
      }
      loadArticle(savedArticle)
      const newUrl = routes.adminKbArticleEdit(String(res.data.article.id))
      if (id === 'new') {
        toastSuccess(options?.successMessage ?? 'New article saved')
        navigate(newUrl)
      } else {
        toastSuccess(options?.successMessage ?? 'Saved')
        window.history.pushState({}, '', newUrl)
      }
      return true
    }

    toastError(res.error ?? 'Failed to save')
    return false
  }

  async function handleSaveDraft() {
    if (!isDirty) return

    const publishStatus = published
    const draftToSave = normalizeDraft(workingDraft, contentPublic)
    const savedAsDraft = draftToSave != null
    await persistArticle(
      buildArticleBody({
        contentDraft: draftToSave,
        published: publishStatus,
      }),
      {
        expectedPublished: publishStatus,
        successMessage: savedAsDraft
          ? (publishStatus ? 'Draft saved' : 'Saved — not published yet')
          : 'Saved',
      },
    )
  }

  const handleSaveDraftRef = useRef(handleSaveDraft)
  handleSaveDraftRef.current = handleSaveDraft

  useEffect(() => {
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.repeat || ev.altKey || ev.shiftKey) return
      if (!(ev.metaKey || ev.ctrlKey)) return
      if (ev.key.toLowerCase() !== 's') return

      ev.preventDefault()
      void handleSaveDraftRef.current()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  function reloadSavedFields() {
    if (!article) return

    const pub = article.contentPublic ?? ''
    const draft = article.contentDraft ?? null

    setPath(article.path ?? '')
    setTitle(article.title ?? '')
    setSubtitle(article.subtitle ?? '')
    setRedirectUrl(article.redirectUrl ?? '')
    setPublishAt(article.publishAt ? String(article.publishAt) : '')
    setUnpublishAt(article.unpublishAt ? String(article.unpublishAt) : '')
    setPublished(article.published ?? false)

    if (id === 'new' && draft == null && pub === '') {
      setWorkingDraft(NEW_ARTICLE_TEMPLATE)
    } else {
      setWorkingDraft(draft ?? pub)
    }
  }

  function handleDiscard() {
    if (!isDirty) return
    if (!confirm('Discard unsaved changes?')) return

    reloadSavedFields()
    toastInfo('Unsaved changes discarded')
  }

  async function handleRevert() {
    if (!hasSavedDraft || id === 'new') return
    if (!confirm(
      'Discard the saved draft and revert to the published version?\n\n'
      + 'The published article will stay as it is. This cannot be undone.',
    )) return

    await persistArticle(
      buildArticleBody({
        contentDraft: null,
        contentPublic,
        published,
      }),
      {
        successMessage: 'Reverted to published version',
        expectedPublished: published,
      },
    )
  }

  async function handlePublish() {
    if (!confirm('Publish the current draft to the live site?')) return

    const publicText = workingDraft
    await persistArticle(
      buildArticleBody({
        contentPublic: publicText,
        contentDraft: null,
        published: true,
      }),
      { successMessage: 'Published' },
    )
  }

  async function handleUnpublish() {
    if (!confirm('Unpublish this article? It will no longer be visible on the public site.')) return

    await persistArticle(
      buildArticleBody({ published: false }),
      { successMessage: 'Unpublished' },
    )
  }

  async function handleRetire() {
    if (!confirm('Retire this article? It will be hidden from normal listings.')) return

    await persistArticle(
      buildArticleBody({ retiredAt: new Date().toISOString() }),
      { successMessage: 'Retired' },
    )
  }

  async function handleUnretire() {
    if (!confirm('Un-retire this article? It will appear in normal listings again.')) return

    await persistArticle(
      buildArticleBody({ retiredAt: null }),
      { successMessage: 'Un-retired' },
    )
  }

  function handleView() {
    const articlePath = (path || article?.path || '').trim()
    if (!articlePath) return
    let url = PUBLIC_WEBSITE + articlePath
    if (!published || hasSavedDraft)
      url += '?preview'
    window.open(url, '_blank')
  }

  async function handleDelete() {
    if (!id || id === 'new' || !article) return
    if (!confirm('Delete this article?')) return

    const res = await api.request(kbArticleDeleteSpec, {
      path: { article: id },
    })
    if (res.status === 200) {
      toastWarning('Deleted')
      navigate(routes.adminKbArticles())
    } else {
      toastError(res.error ?? 'Failed to delete')
    }
  }

  const editorValue = showVersionControls
    ? (contentTab === 'draft' ? workingDraft : contentPublic)
    : workingDraft

  const editorCharCount = editorValue.length
  const editorWordCount = (() => {
    const trimmed = editorValue.trim()
    if (!trimmed) return 0
    return trimmed.split(/\s+/).length
  })()

  const editorReadOnly = showVersionControls && contentTab === 'published'

  const previewMarkdown = showVersionControls
    ? (contentTab === 'draft' ? workingDraft : contentPublic)
    : workingDraft

  const markdownViewerProps = {
    articleId: article?.id,
    withinEditor: true as const,
    allowHtml: true as const,
    linksToNewTabs: true as const,
    localLinksTo: routes.adminKbArticleEdit('0') + '?path=',
    headersStartLevel: 2 as const,
  }

  function renderPreviewPane() {
    if (showVersionControls && previewTab === 'differences') {
      const diffNewText = versionsDiverged ? workingDraft : (contentDraft ?? workingDraft)
      return (
        <KbContentDiff
          oldText={contentPublic}
          newText={diffNewText}
        />
      )
    }

    return (
      <MarkdownViewer
        value={previewMarkdown}
        {...markdownViewerProps}
        allowPlugins={showVersionControls && contentTab === 'draft'}
      />
    )
  }

  if (loading) return <main><p>Loading...</p></main>
  if (!article) return <main><p>Article not found</p></main>

  const showSaveDraft = isDirty
  const showDiscard = isDirty
  const showRevert = showVersionControls && hasSavedDraft && contentPublic.length > 0
  const showPublish = hasPendingDraft || !published
  const showUnpublish = published
  const isRetired = !!article.retiredAt
  const showRetire = id !== 'new' && !isRetired
  const showUnretire = id !== 'new' && isRetired

  const previewTabOptions = [
    { value: 'preview' as const, label: 'Preview' },
    {
      value: 'differences' as const,
      label: <>Differences{hasPendingDraft && <span className='Indicator' aria-hidden />}</>,
      disabled: !hasPendingDraft,
    },
  ]

  return (
    <BasicPage
      title={title ? 'Article: ' + title : 'Edit article'}
      className='KbArticleEdit'
      toolbarButtons={<>
        <Button
          label='Articles'
          icon='chevronLeft'
          href={routes.adminKbArticles()}
        />
        <Button
          label='New article'
          icon='plus'
          disabled={id === 'new'}
          href={routes.adminKbArticleNew()}
          onClick={() => toastInfo('New article')}
        />
        {showSaveDraft && (
          <Button
            variant='primary'
            label='Save draft'
            icon='save'
            tooltip={/Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘S' : 'Ctrl+S'}
            onClick={handleSaveDraft}
          />
        )}
        {showDiscard && (
          <Button
            label='Discard'
            icon='times'
            tooltip='Discard unsaved changes'
            onClick={handleDiscard}
          />
        )}
        {showRevert && (
          <Button
            label='Revert'
            icon='undo'
            tooltip='Discard the saved draft and revert to the published version'
            onClick={handleRevert}
          />
        )}
        {showPublish && (
          <Button
            variant={isRetired ? 'secondary' : 'primary'}
            label='Publish'
            icon='checkCircle'
            onClick={handlePublish}
          />
        )}
        {showUnpublish && (
          <Button
            label='Unpublish'
            icon='lock'
            onClick={handleUnpublish}
          />
        )}
        {showRetire && (
          <Button
            label='Retire'
            icon='mdAssistWalker'
            onClick={handleRetire}
          />
        )}
        {showUnretire && (
          <Button
            variant='primary'
            label='Un-retire'
            icon='mdAssistWalker'
            onClick={handleUnretire}
          />
        )}
        <Button
          label='Delete'
          icon='trash'
          disabled={id === 'new'}
          onClick={handleDelete}
        />
        {id !== 'new' && (
          <Button
            label='View'
            icon='monitorPlay'
            tooltip='Opens a new tab'
            onClick={handleView}
          />
        )}
      </>}
    >

      <div className='WorkArea'>

        <div className='Cutout Wrapped'>
          <TextBox
            label='Path'
            tooltip='/some-folder/and-another/the-article'
            required
            width={425}
            value={path}
            setValue={setPath}
          />
          <TextBox
            label='Title'
            required
            width={425}
            value={title}
            setValue={setTitle}
          />
          <TextBox
            label='Subtitle'
            width={425}
            value={subtitle}
            setValue={setSubtitle}
          />
          <TextBox
            label='Redirect URL'
            tooltip='Absolute URL or site-relative path.'
            width={425}
            value={redirectUrl}
            setValue={setRedirectUrl}
          />
        </div>

        {id !== 'new' && (article?.id ?? 0) > 0 && (
          <div className='Cutout'>
            <KbArticleEditAssets articleId={article.id!} />
          </div>
        )}

        <div className='Cutout FlexColumn Gap2'>

          <div className='EditAndView'>

            <div className='Editor'>
              <div className='EditorToolbar'>
                {showVersionControls && (
                  <TabBar<ContentTab>
                    ariaLabel='Content source'
                    options={[
                      {
                        value: 'draft',
                        label: <>Draft{hasPendingDraft && <span className='Indicator' aria-hidden />}</>,
                      },
                      {
                        value: 'published',
                        label: <>Published{hasPendingDraft && <span className='Indicator' aria-hidden />}</>,
                      },
                    ]}
                    value={contentTab}
                    setValue={setContentTab}
                  />
                )}
                <div className='Counts' aria-live='polite'>
                  <span>{editorCharCount.toLocaleString()} characters</span>
                  <span>{editorWordCount.toLocaleString()} words</span>
                </div>
              </div>
              <TextArea
                autoHeight
                monospace
                spellCheck
                readOnly={editorReadOnly}
                value={editorValue}
                setValue={editorReadOnly ? undefined : setWorkingDraft}
              />
            </div>

            <div className='Viewer'>
              {showVersionControls && (
                <TabBar<PreviewTab>
                  ariaLabel='Preview mode'
                  options={previewTabOptions}
                  value={previewTab}
                  setValue={setPreviewTab}
                />
              )}
              {renderPreviewPane()}
            </div>

          </div>

          <div className='ExtraViewer'>
            {showVersionControls && (
              <TabBar<PreviewTab>
                ariaLabel='Preview mode'
                options={previewTabOptions}
                value={previewTab}
                setValue={setPreviewTab}
              />
            )}
            {renderPreviewPane()}
          </div>

        </div>

      </div>

      <AgentChat
        agent='kb'
        collapsible
        persistenceKey='kbEdit'
        onRefreshEditor={() => { pendingAgentRefreshRef.current = true }}
        onReplyDone={handleAgentRefresh}
        context={{
          page: 'articleEdit',
          articleId: article?.id != null ? String(article.id) : undefined,
          path: path || undefined,
          title: title || undefined,
        }}
      />

    </BasicPage>
  )
}
